"use client";

import { useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { useDebounce } from "@/hooks/use-debounce";
import {
  rankSupportEntries,
  type SupportSearchEntry,
} from "@/lib/support/support-search";
import type { SupportResultGroup, SupportResultLink } from "./support-search-results";

export interface SupportFaq {
  question: string;
  answer: string;
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;
const GUIDE_LIMIT = 4;
const FAQ_LIMIT = 3;
const REMOTE_LIMIT = 6;
const PAGE_CATEGORIES = new Set(["support", "static_page", "trade_page"]);

type RemoteResult = {
  id: string;
  category: string;
  title: string;
  subtitle?: string;
  url: string;
};

function toItem(result: RemoteResult): SupportResultLink {
  return {
    key: result.id,
    title: result.title,
    description: result.subtitle,
    href: result.url,
  };
}

function faqEntries(faqs: ReadonlyArray<SupportFaq>): SupportSearchEntry[] {
  return faqs.map((faq) => ({
    title: faq.question,
    description: faq.answer,
    href: faq.question,
    section: "FAQ",
  }));
}

function groupResults(
  answers: SupportSearchEntry[],
  guides: SupportSearchEntry[],
  remote: RemoteResult[],
): SupportResultGroup[] {
  const guideHrefs = new Set(guides.map((guide) => guide.href));
  const unique = remote.filter((result) => !guideHrefs.has(result.url));
  return [
    {
      label: "Quick answers",
      items: answers.map((faq) => ({ key: faq.href, title: faq.title, answer: faq.description })),
    },
    {
      label: "Guides",
      items: [
        ...guides.map((guide) => ({
          key: guide.href,
          title: guide.title,
          description: guide.title === guide.section ? guide.description : `${guide.section} · ${guide.description}`,
          href: guide.href,
        })),
        ...unique.filter((result) => result.category === "knowledge").map(toItem),
      ],
    },
    {
      label: "Pages and help",
      items: unique.filter((result) => PAGE_CATEGORIES.has(result.category)).map(toItem),
    },
    {
      label: "Blog",
      items: unique.filter((result) => result.category === "blog").map(toItem),
    },
  ];
}

export function useSupportSearch(
  query: string,
  entries: ReadonlyArray<SupportSearchEntry>,
  faqs: ReadonlyArray<SupportFaq>,
) {
  const faqIndex = useMemo(() => faqEntries(faqs), [faqs]);
  const debounced = useDebounce(query.trim(), DEBOUNCE_MS);
  const isActive = debounced.length >= MIN_QUERY_LENGTH;
  const remote = useQuery(
    api.search.queries.search,
    isActive
      ? {
          query: debounced,
          context: "public",
          categories: ["knowledge", "support", "static_page", "trade_page", "blog"],
          limit: REMOTE_LIMIT,
          includeNavigation: false,
        }
      : "skip",
  );
  const groups = useMemo(
    () =>
      isActive
        ? groupResults(
            rankSupportEntries(debounced, faqIndex, FAQ_LIMIT),
            rankSupportEntries(debounced, entries, GUIDE_LIMIT),
            remote?.results ?? [],
          )
        : [],
    [isActive, debounced, faqIndex, entries, remote],
  );
  return {
    groups,
    isActive: query.trim().length >= MIN_QUERY_LENGTH,
    isSearching: isActive && remote === undefined,
  };
}
